import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Save, Loader2, IndianRupee } from "lucide-react";
import { toast } from "react-hot-toast";
import useWhatsapp from "../Services/WhatsappServices/useWhatsapp";

const emptyAddForm = {
  key: "",
  value: "",
};

const keyRegex = /^[A-Z]+$/;
const valueRegex = /^\d*\.?\d*$/;

const normalizeKey = (v) =>
  String(v ?? "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

export default function CampaignChargesModal({
  open,
  onClose,
  userId = "",
  title = "Campaign Charges",
}) {
  const { getCampaignCharges, insertUpdateCampiagnCharges } = useWhatsapp();

  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [rows, setRows] = useState([]);
  const [addForm, setAddForm] = useState(emptyAddForm);

  const scopeLabel = useMemo(
    () => (userId ? `Client: ${userId}` : "Global Charges"),
    [userId]
  );

  const fetchCharges = async () => {
    setLoading(true);
    const res = await getCampaignCharges(userId || "");
    if (res?.success) {
      setRows(
        (res?.data || []).map((item) => ({
          ...item,
          editedValue: String(item?.value ?? ""),
        }))
      );
    } else {
      toast.error(res?.message || "Failed to fetch charges");
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchCharges();
      setAddForm(emptyAddForm);
    }
  }, [open, userId]);

  const handleUpdate = async (row) => {
    const editedValue = String(row?.editedValue ?? "").trim();

    if (!editedValue) {
      toast.error("Value is required");
      return;
    }

    if (!valueRegex.test(editedValue)) {
      toast.error("Value must be numeric");
      return;
    }

    setSavingId(`update-${row?.pk_pricing_id}`);
    const res = await insertUpdateCampiagnCharges({
      type: "0",
      pkid: row?.pk_pricing_id || "",
      key: row?.key || "",
      value: editedValue,
      userid: userId || "",
    });
    setSavingId("");

    if (res?.success) {
      toast.success(res?.message || "Charge updated successfully");
      fetchCharges();
    } else {
      toast.error(res?.message || "Failed to update charge");
    }
  };

  const handleAdd = async () => {
    const finalKey = normalizeKey(addForm.key);
    const finalValue = String(addForm.value ?? "").trim();

    if (!finalKey) {
      toast.error("Key is required");
      return;
    }

    if (!keyRegex.test(finalKey)) {
      toast.error("Key must contain only alphabets in ALL CAPS");
      return;
    }

    if (!finalValue) {
      toast.error("Value is required");
      return;
    }

    if (!valueRegex.test(finalValue)) {
      toast.error("Value must be numeric");
      return;
    }

    setSavingId("insert");
    const res = await insertUpdateCampiagnCharges({
      type: "1",
      pkid: "",
      key: finalKey,
      value: finalValue,
      userid: userId || "",
    });
    setSavingId("");

    if (res?.success) {
      toast.success(res?.message || "Charge added successfully");
      setAddForm(emptyAddForm);
      fetchCharges();
    } else {
      toast.error(res?.message || "Failed to add charge");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-3">
      <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl">
        {/* header */}
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">{scopeLabel}</p>
          </div>

          <button
            className="rounded-xl border p-2 hover:bg-gray-50"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* body */}
        <div className="max-h-[75vh] overflow-auto p-5">
          {/* add new */}
          <div className="mb-5 rounded-2xl border bg-gray-50 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Plus size={16} />
              Add New Charge
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Template Type
                </label>
                <input
                  type="text"
                  value={addForm.key}
                  onChange={(e) =>
                    setAddForm((s) => ({
                      ...s,
                      key: normalizeKey(e.target.value),
                    }))
                  }
                  placeholder="MARKETING"
                  className="h-10 w-full rounded-xl border px-3 text-sm outline-none ring-emerald-200 focus:ring-2"
                />
             
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Price
                </label>
                <input
                  type="text"
                  value={addForm.value}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next === "" || valueRegex.test(next)) {
                      setAddForm((s) => ({ ...s, value: next }));
                    }
                  }}
                  placeholder="0.900000"
                  className="h-10 w-full rounded-xl border px-3 text-sm outline-none ring-emerald-200 focus:ring-2"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleAdd}
                  disabled={savingId === "insert"}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {savingId === "insert" ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Plus size={16} />
                  )}
                  Add Charge
                </button>
              </div>
            </div>
          </div>

          {/* charges list */}
          <div className="overflow-hidden rounded-2xl border">
            <div className="overflow-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#01C37D] text-white">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Template Category</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Pricing Type</th>
                    <th className="px-4 py-3 font-semibold">Country</th>
                    <th className="px-4 py-3 font-semibold">Created On</th>
                    <th className="px-4 py-3 font-semibold text-center">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        Loading charges...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No charges found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => {
                      const isSaving = savingId === `update-${row?.pk_pricing_id}`;

                      return (
                        <tr key={`${row?.pk_pricing_id}-${idx}`} className="border-t">
                          <td className="px-4 py-3 font-medium text-gray-800">
                            {row?.key || "—"}
                          </td>

                          <td className="px-4 py-3">
                            <div className="relative max-w-[180px]">
                              <IndianRupee
                                size={14}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                              />
                              <input
                                type="text"
                                value={row?.editedValue ?? ""}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  if (next === "" || valueRegex.test(next)) {
                                    setRows((prev) =>
                                      prev.map((r) =>
                                        r.pk_pricing_id === row.pk_pricing_id
                                          ? { ...r, editedValue: next }
                                          : r
                                      )
                                    );
                                  }
                                }}
                                className="h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none ring-emerald-200 focus:ring-2"
                              />
                            </div>
                          </td>

                          <td className="px-4 py-3">{row?.pricing_type || "—"}</td>
                          <td className="px-4 py-3">{row?.country_code || "—"}</td>
                          <td className="px-4 py-3">{row?.created_on || "—"}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleUpdate(row)}
                              disabled={isSaving}
                              className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-xs font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                              {isSaving ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Save size={14} />
                              )}
                              Update
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}